import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import { formatPrice, isPriceOnRequest, pluralize } from '@alkor/utils';
import { IconCart } from '@/components/icons';
import {
  EmptyState, Notice, Qty, Rail, StatusChip, Thumb, TileSkeleton,
} from '@/components/ui';
import { MainButton } from '@/components/MainButton';

export function CartScreen() {
  const navigate = useNavigate();
  const { cart, cartLoading, setQuantity, removeItem, clearCart, busy } = useShop();

  useEffect(() => { track('cart_view'); }, []);

  // Блок «с этим берут» заполняет пустоту при одной-двух позициях.
  // Аксессуары берутся к первой позиции корзины.
  const firstSlug = cart?.items[0]?.product.slug;
  const relatedQuery = useQuery({
    queryKey: ['related', firstSlug],
    queryFn: () => api.related(firstSlug!),
    enabled: Boolean(firstSlug),
    staleTime: 300_000,
  });

  const alsoViewed = useMemo(() => {
    const inCartIds = new Set(cart?.items.map((i) => i.product.id));
    return (relatedQuery.data?.accessories ?? [])
      .concat(relatedQuery.data?.similar ?? [])
      .filter((p) => !inCartIds.has(p.id))
      .slice(0, 6);
  }, [relatedQuery.data, cart]);

  const popular = useQuery({ queryKey: ['home'], queryFn: api.home, staleTime: 60_000 });

  if (cartLoading) return <div className="block" style={{ marginTop: 20 }}><TileSkeleton count={2} /></div>;

  if (!cart || cart.items.length === 0) {
    return (
      <>
        <EmptyState
          icon={<IconCart size={24} />}
          title="Корзина пуста"
          text="Добавьте снаряжение из каталога — корзина сохранится, даже если закроете приложение."
          action={<button type="button" className="secbtn" onClick={() => navigate('/')}>Перейти в каталог</button>}
        />
        <Rail title="Хиты продаж" items={popular.data?.popular ?? []} />
      </>
    );
  }

  // Товар без цены в заказ не попадает: его нужно убрать или уточнить цену у менеджера
  const requestItems = cart.items.filter((i) => isPriceOnRequest(i.product.price));
  const blocked = cart.items.some((i) => i.product.availability === 'out_of_stock') || requestItems.length > 0;

  return (
    <>
      <div className="screen-title">Корзина</div>
      <div className="screen-sub">
        {pluralize(cart.count, 'позиция', 'позиции', 'позиций')} · сохраняется на сервере
      </div>

      {cart.warnings.length > 0 && (
        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          {cart.warnings.map((w, i) => (
            <Notice key={i} variant={w.code === 'OUT_OF_STOCK' ? 'warn' : 'info'}>
              {w.code === 'OUT_OF_STOCK'
                ? `«${w.productName}» закончился, пока вы выбирали — удалите позицию, чтобы оформить заказ`
                : `«${w.productName}»: цена изменилась ${w.detail ?? ''}`}
            </Notice>
          ))}
        </div>
      )}

      {requestItems.length > 0 && (
        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          {requestItems.map((i) => (
            <Notice key={i.id} variant="warn">
              «{i.product.name}» — цена по запросу, в заказ такой товар добавить нельзя.
              Удалите позицию или уточните цену у менеджера.
            </Notice>
          ))}
        </div>
      )}

      <div className="block" style={{ marginTop: 16 }}>
        <div className="plist">
          {cart.items.map((item) => (
            <div className="citem" key={item.id}>
              <Thumb product={item.product} />
              <div>
                <div className="citem__top">
                  <div>
                    <div className="citem__name">{item.product.name}</div>
                    <div style={{ marginTop: 5 }}>
                      <StatusChip
                        status={item.product.availability}
                        leadTimeDays={item.product.leadTimeDays}
                      />
                    </div>
                  </div>
                  {isPriceOnRequest(item.product.price)
                    ? <div className="price price--request">По запросу</div>
                    : <div className="price">{formatPrice(item.lineTotal)}</div>}
                </div>
                <div className="citem__bot">
                  <Qty
                    value={item.quantity}
                    disabled={busy}
                    onChange={(q) => void setQuantity(item.id, q)}
                  />
                  <button type="button" className="linkbtn" onClick={() => void removeItem(item.id)}>
                    Удалить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="button" className="linkbtn" style={{ marginTop: 12 }} onClick={() => void clearCart()}>
          Очистить корзину
        </button>
      </div>

      <Rail title="С этим берут" items={alsoViewed} />

      <div className="summary">
        <div className="srow">
          <span>Товары, {pluralize(cart.count, 'позиция', 'позиции', 'позиций')}</span>
          <span style={{ fontFamily: 'var(--f-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {formatPrice(cart.itemsTotal)}
          </span>
        </div>
        <div className="srow"><span>Доставка</span><span>рассчитает менеджер</span></div>
        <div className="srow srow--total">
          <span>Итого</span>
          <span className="price">{formatPrice(cart.itemsTotal)}</span>
        </div>
      </div>

      <div className="pad" />

      <MainButton
        text={requestItems.length > 0 ? 'Удалите товары без цены' : blocked ? 'Удалите недоступные позиции' : 'Оформить'}
        suffix={blocked ? undefined : formatPrice(cart.itemsTotal)}
        disabled={blocked}
        onClick={() => navigate('/checkout')}
      />
    </>
  );
}
